import React, {useCallback} from 'react';
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  Paper,
  PaperProps,
  TextField
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Form, Formik} from 'formik';
import {useAppSelector} from '@/redux-store/store';
import {Company} from '@/types/company';
import {ExtendedModule} from '@/types/module';
import {setCompanyModules} from '@/rest/admin/companies/set-company-modules';
import i18n from '@/i18n/i18n';
import {SupportedLanguage} from '@/types/common';

import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';

const StyledPaper: React.FC<PaperProps> = (props) => {
  return <Paper {...props} style={{maxWidth: 300, margin: 'auto'}} />;
};

interface ModuleType extends ExtendedModule {
  key: string;
}

interface ManageModulesFormProps {
  onCancel?: () => void;
  onSubmit?: (updatedData: ExtendedModule) => void;
  onError?: (error: Error) => void;
  companyData: Company;
  showOnlyTwoButtons?: boolean;
}

const ManageModulesForm: React.FC<ManageModulesFormProps> = ({
  onCancel,
  onSubmit,
  onError,
  companyData,
  showOnlyTwoButtons
}) => {
  const {t} = useTranslation('health_modules');

  const currentLanguage = i18n.language as SupportedLanguage;

  const formModules = useAppSelector((state) => state.modules.formModules);

  const currentCompanyId = companyData._id;

  const filteredModules = formModules.filter(
    (module) => !module.createdBy || module.createdBy._id === currentCompanyId
  );

  const submitForm = useCallback(
    (values: {modules: typeof initialValues}) => {
      const companyId = companyData._id;
      const moduleKeys = values.modules
        ? values.modules.map((module) => module.key)
        : [];

      if (companyId) {
        setCompanyModules(companyId, moduleKeys)
          .then((response) => {
            if (response) {
              if (onSubmit) {
                onSubmit(response);
              }
            } else {
              if (onError) {
                onError(new Error('Error while setting modules'));
              }
            }
          })
          .catch((error) => {
            console.error('Error:', error);
            if (onError) {
              onError(error as Error);
            }
          });
      }
    },
    [onError, onSubmit, companyData._id]
  );

  const initialValues = companyData.modules;

  return (
    <>
      <Formik
        initialValues={{modules: initialValues}}
        onSubmit={(values) => {
          submitForm(values);
        }}
      >
        {({values, setFieldValue, resetForm}) => {
          // console.log('values', values);
          return (
            <Form>
              <FormControl sx={{width: '100%'}} variant="outlined" fullWidth>
                <Grid container spacing={2} my={1} marginTop={3}>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      PaperComponent={StyledPaper}
                      noOptionsText={t('no_options')}
                      options={filteredModules}
                      getOptionLabel={(moduleKey: ModuleType) => {
                        const module = filteredModules.find(
                          (m) => m.key === moduleKey.key
                        );
                        if (
                          module &&
                          module.label &&
                          module.label[currentLanguage]
                        ) {
                          return `${module.label[currentLanguage]}-${module.key}`;
                        }
                        return `${t(`module.${moduleKey.key}`)}-${
                          moduleKey.key
                        }`;
                      }}
                      value={values.modules}
                      isOptionEqualToValue={(option, value) =>
                        option.key === value.key
                      }
                      onChange={(_, newValue) =>
                        setFieldValue('modules', newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="modules"
                          variant="outlined"
                          label={t('modules')}
                          fullWidth
                        />
                      )}
                      renderOption={(props, module, {selected}) => (
                        <li {...props}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              justifyContent: 'space-between'
                            }}
                          >
                            <div>
                              {module.label && module.label[currentLanguage]
                                ? module.label[currentLanguage]
                                : t(`module.${module.key}`)}
                              <small
                                style={{display: 'block', fontSize: '0.7em'}}
                              >
                                {module.key}
                              </small>
                            </div>
                            <Checkbox
                              checkedIcon={<CheckIcon fontSize="small" />}
                              style={{marginRight: 8}}
                              checked={selected}
                            />
                          </div>
                        </li>
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  direction={{xs: 'column-reverse', sm: 'row'}}
                  justifyContent="center"
                  spacing={2}
                  sx={{
                    marginTop: {xs: 2, sm: 5},
                    marginBottom: 5
                  }}
                >
                  {!showOnlyTwoButtons && (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        order: {xs: 1, sm: 0}
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onCancel}
                        sx={{width: {xs: '50%', sm: '70%'}}}
                      >
                        {t('cancel')}
                      </Button>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                    sm={showOnlyTwoButtons ? 6 : 4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      order: {xs: 2, sm: showOnlyTwoButtons ? 1 : 2},
                      marginBottom: {xs: 2, sm: 0}
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => resetForm()}
                      sx={{width: {xs: '50%', sm: '70%'}}}
                    >
                      {t('reset')}
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={showOnlyTwoButtons ? 6 : 4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: {xs: 2, sm: 0},
                      order: {
                        xs: 3,
                        sm: showOnlyTwoButtons ? 2 : 3
                      }
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      sx={{width: {xs: '50%', sm: '70%'}}}
                    >
                      {t('save')}
                    </Button>
                  </Grid>
                </Grid>
              </FormControl>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default ManageModulesForm;
